import { PublishedWithChanges } from '@mui/icons-material';
import { Box, Card, Chip } from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { To } from 'react-router-dom';
import { AppCardThumbnail } from '~/components/AppCard';
import { Typography } from '~/components/Typography';
import { CardActionsTitleText } from '~/components/cards';
import { CardActions, CardActionsBottom, CardActionsTop } from '~/components/cards/styles';
import { Link } from '~/components/link';
import { TagList } from '~/components/tag';
import { OverlayContainer, ThumbnailContainer } from '~/pages/devices/list/lib';
import type { DesignList__Design as Design } from '../DesignList.generated';

export interface StudioCardProps {
  children?: ReactNode;
  design: Design;
  to?: To;
}

export const StudioCard = ({ children, design, to }: StudioCardProps) => {
  const { t } = useTranslation();

  const thumbnail = (
    <ThumbnailContainer>
      <AppCardThumbnail app={design} />
      <OverlayContainer className="overlay-section">
        <Box sx={{ paddingY: 0.5, maxWidth: '100%' }}>
          <TagList abbreviate item={design} sx={{ flexDirection: 'column' }} tags={design.tags} />
        </Box>
      </OverlayContainer>
    </ThumbnailContainer>
  );

  return (
    <Card>
      {to ? (
        <Link
          to={to}
          // The overlay's tags would otherwise join the link's name.
          aria-label={design.name}
          underline="none"
          sx={{ display: 'block' }}
        >
          {thumbnail}
        </Link>
      ) : (
        thumbnail
      )}
      <CardActions>
        <CardActionsTop>
          <CardActionsTitleText title={design.name} />
        </CardActionsTop>
        <CardActionsBottom>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              fontSize: 13,
              justifyContent: 'space-between',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              pr: 0.5,
            }}
          >
            <Typography variant="subtitle2">
              {design.width}x{design.height}
            </Typography>
            {design.publishedApp && (
              <Chip
                icon={<PublishedWithChanges />}
                label={t('pages.studio.badge.published')}
                color="success"
              />
            )}
          </Box>
          {children}
        </CardActionsBottom>
      </CardActions>
    </Card>
  );
};
